import { router } from "@inertiajs/react"
import { useEffect } from "react"

import { getCableConsumer } from "@/lib/cable"

/**
 * OKS-406 — Creator real-time refetch hook.
 *
 * Subscribes to `CreatorChannel` and, on each lightweight `refetch`
 * signal, fires a targeted Inertia partial reload of ONLY the shared
 * props named in the signal (`unread_notifications_count` /
 * `pending_rank_up` / `pending_badge`). The payload is never rendered
 * directly — the reload recomputes the props server-side in the
 * creator's active-brand context, so all existing render / modal / i18n
 * / brand-isolation logic is reused and no brand data crosses the wire.
 *
 * Behaviour by state (UX Spec §3 — all invisible except success):
 *   - **connected / reconnected:** fire ONE catch-up reload so any
 *     signal missed while the socket was down is reflected immediately.
 *   - **received:** partial-reload exactly the requested props.
 *   - **transport unavailable / down:** silent. The hook degrades to a
 *     no-op and the existing 30s `usePollUnreadCount()` + next-render
 *     path covers the gap. No error, no connection-status UI.
 *
 * Idempotent with the poll: each reload returns the canonical
 * server-side value (Inertia replaces the prop, never increments), so a
 * poll and a live signal landing together cannot double-count.
 *
 * Mount ONCE at the creator-shell level alongside `usePollUnreadCount()`.
 */
const CATCH_UP_PROPS = [
  "unread_notifications_count",
  "pending_rank_up",
  "pending_badge",
  // OKS-555 — catch up a live "your moment" cheer missed while the socket
  // was down (the `received` path handles the per-signal reload array).
  "pending_cheer",
]

interface RefetchSignal {
  type?: string
  reload?: unknown
}

export function useRealtimeRefetch() {
  useEffect(() => {
    let consumer
    try {
      consumer = getCableConsumer()
    } catch {
      // Transport unavailable (e.g. WebSocket blocked) — silently fall
      // back to the poll path. Real-time is an enhancement, never a
      // dependency.
      return
    }

    const subscription = consumer.subscriptions.create(
      { channel: "CreatorChannel" },
      {
        connected() {
          // Catch-up refetch on initial connect AND every reconnect, so
          // updates missed during a drop land without a page reload.
          router.reload({ only: CATCH_UP_PROPS, preserveUrl: true })
        },
        received(data: RefetchSignal) {
          if (data?.type !== "refetch") return

          const only = Array.isArray(data.reload)
            ? data.reload.filter((p): p is string => typeof p === "string")
            : []
          if (only.length === 0) return

          router.reload({ only, preserveUrl: true })
        },
      },
    )

    return () => {
      subscription.unsubscribe()
    }
  }, [])
}
